import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Plus } from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { ClientAccessScope, ClientValue, PortalPatient, PortalTherapist } from "../api/contracts";
import {
  ActionsColumnHeader,
  ContentSection,
  PortalDataTable,
  PortalTableColumn,
} from "./DataDisplay";
import { ErrorState, LoadingState } from "./FeedbackStates";
import { RowActionMenu } from "./HomeworkControls";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";

type EditorMode = { kind: "create" } | { kind: "edit"; value: ClientValue };

function replaceValue(
  values: ClientValue[],
  savedValue: ClientValue,
): ClientValue[] {
  const index = values.findIndex((value) => value.id === savedValue.id);
  if (index === -1) return [...values, savedValue];
  return values.map((value) =>
    value.id === savedValue.id ? savedValue : value,
  );
}

function ClientValueEditor({
  clinicianCode,
  patientId,
  mode,
  onCancel,
  onSaved,
}: {
  clinicianCode: string;
  patientId: string;
  mode: EditorMode;
  onCancel: () => void;
  onSaved: (value: ClientValue) => void;
}) {
  const mutation = usePortalMutation();
  const submittingRef = useRef(false);
  const value = mode.kind === "edit" ? mode.value : undefined;
  const [valueName, setValueName] = useState(value?.valueName ?? "");
  const [whyImportant, setWhyImportant] = useState(value?.whyImportant ?? "");
  const [valueNameError, setValueNameError] = useState<string>();
  const isSubmitting = mutation.state.status === "pending";
  const heading = mode.kind === "create" ? "Add Value" : "Edit Value";
  const headingId = React.useId();
  const valueInputId = React.useId();
  const whyInputId = React.useId();
  const valueHelpId = React.useId();
  const whyHelpId = React.useId();
  const valueErrorId = React.useId();

  const clearErrors = () => {
    setValueNameError(undefined);
    mutation.reset();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current || isSubmitting) return;

    const normalizedValueName = valueName.trim();
    const normalizedWhyImportant = whyImportant.trim();
    if (!normalizedValueName) {
      setValueNameError("Value is required.");
      return;
    }

    submittingRef.current = true;
    setValueNameError(undefined);
    mutation.reset();
    try {
      const result = await mutation.run(
        mode.kind === "create" ? "create" : `edit:${mode.value.id}`,
        (signal) =>
          mode.kind === "create"
            ? clinicianPortalApi.createClientValue(
                clinicianCode,
                patientId,
                normalizedValueName,
                normalizedWhyImportant,
                signal,
              )
            : clinicianPortalApi.updateClientValue(
                clinicianCode,
                patientId,
                mode.value.id,
                normalizedValueName,
                normalizedWhyImportant,
                signal,
              ),
      );
      if (result) onSaved(result.value);
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <section className="cp-client-value-editor" aria-labelledby={headingId}>
      <div className="cp-client-value-editor__header">
        <h3 id={headingId}>{heading}</h3>
        <p>Capture the client’s own Value and why it matters to them.</p>
      </div>
      <form onSubmit={handleSubmit} noValidate>
        <div className="cp-form-field">
          <label htmlFor={valueInputId}>
            Value <span aria-hidden="true">*</span>
          </label>
          <p
            className="cp-form-help cp-client-value-form-help"
            id={valueHelpId}
          >
            Enter a short value, such as Independence, Family, Friendship,
            Learning, Faith, or Courage.
          </p>
          <input
            id={valueInputId}
            className="cp-form-input"
            type="text"
            value={valueName}
            required
            disabled={isSubmitting}
            aria-invalid={Boolean(valueNameError)}
            aria-describedby={
              valueNameError
                ? `${valueHelpId} ${valueErrorId}`
                : valueHelpId
            }
            onChange={(event) => {
              clearErrors();
              setValueName(event.target.value);
            }}
          />
          {valueNameError ? (
            <p className="cp-form-error" id={valueErrorId}>
              {valueNameError}
            </p>
          ) : null}
        </div>
        <div className="cp-form-field">
          <label htmlFor={whyInputId}>Why This Value Is Important</label>
          <p
            className="cp-form-help cp-client-value-form-help"
            id={whyHelpId}
          >
            Describe why this matters to the client, preferably in the
            client&apos;s own words and first-person voice.
            <br />
            For example, I want to handle everyday activities more
            independently and rely less on others because of OCD.
          </p>
          <textarea
            id={whyInputId}
            className="cp-form-input cp-form-textarea"
            value={whyImportant}
            disabled={isSubmitting}
            aria-describedby={whyHelpId}
            onChange={(event) => {
              clearErrors();
              setWhyImportant(event.target.value);
            }}
          />
        </div>
        {mutation.state.status === "error" ? (
          <p className="cp-form-error cp-form-error--panel" role="alert">
            The Value could not be saved. Your changes are still here; please
            try again.
          </p>
        ) : null}
        <div className="cp-form-actions">
          <button
            type="button"
            className="cp-button cp-button--secondary"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="cp-button cp-button--primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? mode.kind === "create"
                ? "Adding…"
                : "Saving…"
              : mode.kind === "create"
                ? "Add Value"
                : "Save"}
          </button>
        </div>
      </form>
    </section>
  );
}

function ArchiveValueDialog({
  value,
  isArchiving,
  hasError,
  onCancel,
  onConfirm,
}: {
  value: ClientValue | null;
  isArchiving: boolean;
  hasError: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = React.useId();
  const descriptionId = React.useId();

  useEffect(() => {
    if (value) cancelButtonRef.current?.focus();
  }, [value]);

  if (!value) return null;

  return (
    <div
      className="cp-confirm-dialog__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isArchiving) onCancel();
      }}
    >
      <div
        className="cp-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !isArchiving) {
            event.preventDefault();
            onCancel();
          }
        }}
      >
        <h2 id={titleId}>Archive this Value?</h2>
        <p id={descriptionId}>
          This Value will no longer be available for new assignments, but it
          will remain available for historical records.
        </p>
        {hasError ? (
          <p className="cp-confirm-dialog__error" role="alert">
            The Value could not be archived. Please try again.
          </p>
        ) : null}
        <div className="cp-confirm-dialog__actions">
          <button
            ref={cancelButtonRef}
            type="button"
            className="cp-button cp-button--secondary"
            disabled={isArchiving}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cp-button cp-button--danger"
            disabled={isArchiving}
            onClick={onConfirm}
          >
            {isArchiving ? "Archiving…" : "Archive Value"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ClientValuesSection({
  clinicianCode,
  patientId,
  onContextLoaded,
  readOnly = false,
  accessScope = "assigned",
}: {
  clinicianCode: string;
  patientId: string;
  onContextLoaded?: (
    therapist: PortalTherapist,
    patient: PortalPatient,
  ) => void;
  readOnly?: boolean;
  accessScope?: ClientAccessScope;
}) {
  const request = useCallback(
    (signal: AbortSignal) =>
      accessScope === "team"
        ? clinicianPortalApi.getClientValues(
            clinicianCode,
            patientId,
            signal,
            "team",
          )
        : clinicianPortalApi.getClientValues(clinicianCode, patientId, signal),
    [accessScope, clinicianCode, patientId],
  );
  const state = usePortalData(request);
  const mutation = usePortalMutation();
  const [values, setValues] = useState<ClientValue[]>([]);
  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<ClientValue | null>(null);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist && data.patient,
  );
  const isMutating = mutation.state.status === "pending";

  useEffect(() => {
    if (data) setValues(data.values);
  }, [data]);

  useEffect(() => {
    if (data?.therapist && data.patient && onContextLoaded) {
      onContextLoaded(data.therapist, data.patient);
    }
  }, [data?.patient, data?.therapist, onContextLoaded]);

  const activeValues = values.filter((value) => value.enabled);
  const archivedValues = values.filter((value) => !value.enabled);

  const activeValueColumns = useMemo<
    readonly PortalTableColumn<ClientValue>[]
  >(
    () => [
      {
        key: "actions",
        header: <ActionsColumnHeader />,
        headerLabel: "Actions",
        initialWidth: 60,
        minWidth: 60,
        resizable: false,
        cellClassName: "cp-table-cell--compact",
        render: (value) => (
          <RowActionMenu
            clinicianCode={clinicianCode}
            activityName={value.valueName}
            canEdit={!isMutating && !editor}
            onEdit={() => {
              mutation.reset();
              setEditor({ kind: "edit", value });
            }}
            showDuplicate={false}
            canDelete={!isMutating && !editor}
            onDelete={() => {
              mutation.reset();
              setArchiveTarget(value);
            }}
            deleteLabel="Archive"
            deletingLabel="Archiving…"
          />
        ),
      },
      {
        key: "value",
        header: "Value",
        initialWidth: 300,
        minWidth: 200,
        render: (value) => (
          <span className="cp-homework-name">{value.valueName}</span>
        ),
      },
      {
        key: "whyImportant",
        header: "Why This Value Is Important",
        initialWidth: 760,
        minWidth: 360,
        cellClassName: "cp-table-cell--multiline",
        render: (value) =>
          value.whyImportant || (
            <span className="cp-empty-value">No reason has been provided.</span>
          ),
      },
    ],
    [clinicianCode, editor, isMutating, mutation],
  );

  const saveLocalValue = (savedValue: ClientValue) => {
    setValues((current) => replaceValue(current, savedValue));
    setEditor(null);
  };

  const setEnabled = async (value: ClientValue, enabled: boolean) => {
    if (readOnly || isMutating) return;
    mutation.reset();
    const action = `${enabled ? "reactivate" : "archive"}:${value.id}`;
    const result = await mutation.run(action, (signal) =>
      clinicianPortalApi.setClientValueEnabled(
        clinicianCode,
        patientId,
        value.id,
        enabled,
        signal,
      ),
    );
    if (!result) return;
    setValues((current) => replaceValue(current, result.value));
    if (!enabled) setArchiveTarget(null);
  };

  return (
    <ContentSection
      title="Values"
      hideTitle
      actions={
        available && !readOnly ? (
          <button
            type="button"
            className="cp-button cp-button--primary"
            disabled={isMutating || Boolean(editor)}
            onClick={() => {
              mutation.reset();
              setEditor({ kind: "create" });
            }}
          >
            <Plus aria-hidden="true" />
            Add Value
          </button>
        ) : null
      }
    >
      <p className="cp-content-section__description">
        Identify what matters most to this client and why. Values can be linked
        to homework assignments to connect practice with personally meaningful
        goals.
      </p>
      {state.status === "loading" ? (
        <LoadingState label="Loading Client Values" />
      ) : null}
      {state.status === "error" ? (
        <ErrorState error={state.error} onRetry={state.refresh} />
      ) : null}
      {data && !available ? (
        <ErrorState error={new Error("Client Values are not available")} />
      ) : null}
      {available ? (
        <div className="cp-client-values">
          {editor && !readOnly ? (
            <ClientValueEditor
              key={editor.kind === "edit" ? editor.value.id : "create"}
              clinicianCode={clinicianCode}
              patientId={patientId}
              mode={editor}
              onCancel={() => setEditor(null)}
              onSaved={saveLocalValue}
            />
          ) : null}

          {activeValues.length ? (
            <PortalDataTable
              columns={readOnly ? activeValueColumns.slice(1) : activeValueColumns}
              records={activeValues}
              getRecordKey={(value) => value.id}
              caption="Active Values"
            />
          ) : (
            <div className="cp-client-values__empty">
              <p>No Values have been added for this client.</p>
            </div>
          )}

          {archivedValues.length ? (
            <div className="cp-client-values__archived">
              <button
                type="button"
                className="cp-client-values__archive-toggle"
                aria-expanded={showArchived}
                onClick={() => setShowArchived((current) => !current)}
              >
                {showArchived ? "Hide" : "Show"} archived Values (
                {archivedValues.length})
              </button>
              {showArchived ? (
                <div
                  className="cp-client-value-list"
                  aria-label="Archived Values"
                >
                  {archivedValues.map((value) => {
                    const action = `reactivate:${value.id}`;
                    const isReactivating =
                      mutation.state.status === "pending" &&
                      mutation.state.action === action;
                    const hasError =
                      mutation.state.status === "error" &&
                      mutation.state.action === action;
                    return (
                      <article
                        className="cp-client-value-card cp-client-value-card--archived"
                        key={value.id}
                      >
                        <div className="cp-client-value-card__content">
                          <div className="cp-client-value-card__title">
                            <h3>{value.valueName}</h3>
                            <span>Archived</span>
                          </div>
                          <p>
                            {value.whyImportant || (
                              <span className="cp-empty-value">
                                No reason has been provided.
                              </span>
                            )}
                          </p>
                          {hasError ? (
                            <p
                              className="cp-client-value-card__error"
                              role="alert"
                            >
                              The Value could not be reactivated. Please try
                              again.
                            </p>
                          ) : null}
                        </div>
                        {!readOnly ? <div className="cp-client-value-card__actions">
                          <button
                            type="button"
                            className="cp-button cp-button--secondary"
                            aria-label={`Reactivate ${value.valueName}`}
                            disabled={isMutating || Boolean(editor)}
                            onClick={() => setEnabled(value, true)}
                          >
                            {isReactivating ? "Reactivating…" : "Reactivate"}
                          </button>
                        </div> : null}
                      </article>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {!readOnly ? <ArchiveValueDialog
        value={archiveTarget}
        isArchiving={Boolean(
          archiveTarget &&
            mutation.state.status === "pending" &&
            mutation.state.action === `archive:${archiveTarget.id}`,
        )}
        hasError={Boolean(
          archiveTarget &&
            mutation.state.status === "error" &&
            mutation.state.action === `archive:${archiveTarget.id}`,
        )}
        onCancel={() => {
          setArchiveTarget(null);
          mutation.reset();
        }}
        onConfirm={() => {
          if (archiveTarget) setEnabled(archiveTarget, false);
        }}
      /> : null}
    </ContentSection>
  );
}
